import { getWebsocketURI } from '../../../../api/convo-ai/getWebsocketURI';

export enum ConvoAIWebSocketServerMessageType {
	TranscriptionResponseDelta = 'transcription.response.delta',
	TranscriptionResponseCompleted = 'transcription.response.completed',
	TranscriptionItemCreated = 'transcription.item.created',
	TranscriptionSessionCreated = 'transcription.session.created',
}

export enum ConvoAIWebSocketClientMessageType {
	AudioBufferAppend = 'audio.buffer.append',
	AudioBufferCommit = 'audio.buffer.commit',
	AudioSessionUpdate = 'audio.session.update',
}

export type ConvoAIWebSocketServerMessage =
	| {
			content_index: number;
			delta: string;
			type: ConvoAIWebSocketServerMessageType.TranscriptionResponseDelta;
	  }
	| {
			type: ConvoAIWebSocketServerMessageType.TranscriptionItemCreated;
	  }
	| {
			type: ConvoAIWebSocketServerMessageType.TranscriptionSessionCreated;
	  }
	| {
			transcript?: string;
			speaker?: string;
			type: ConvoAIWebSocketServerMessageType.TranscriptionResponseCompleted;
	  };

export class ConvoAIWebSocketAPI {
	private socket: WebSocket;

	constructor(
		cloudID: string,
		product: string,
		experience: string,
		private readonly onMessage: (event: ConvoAIWebSocketServerMessage) => void,
		private readonly websocketURI: string = getWebsocketURI(),
	) {
		this.socket = new WebSocket(
			`${this.websocketURI}/wss/${cloudID}/ai-feature/audio/realtime-transcriptions?product=${product}&experience=${experience}`,
		);
		this.socket.addEventListener('message', this.onServerMessage);
	}

	updateTranscriptionConfig(
		instructions: string = `Transcribe speech to text. You may get wake words like 'Hey Rovo!' or 'Hey Atlassian!' or 'Rovo, '. Do not include the waking phrase at the beginning of transcription`,
		noise_detection: string = 'near_field',
		vad_type: string = 'semantic_vad',
		eagerness: string = 'medium',
		model: string = 'gpt-4o-transcribe',
		include: string[] = ['item.input_audio_transcription.logprobs'],
	): void {
		// `gpt-realtime-whisper` runs in OpenAI's GA Realtime API as a streaming
		// transcription-only session. Per the OpenAI Realtime VAD guide
		// (https://developers.openai.com/api/docs/guides/realtime-vad), whisper
		// requires `turn_detection` to be omitted or `null` — server VAD is
		// incompatible. Additionally, the `prompt` / `instructions` field is
		// not supported for whisper in GA Realtime, and the BE's gateway has
		// been verified to reject session updates that include those fields.
		//
		// `noise_detection` is also dropped because it implicitly relies on a
		// VAD pipeline. `include` is preserved so future consumers can opt in
		// to logprobs without re-plumbing.
		if (model === 'gpt-realtime-whisper') {
			this.socket.send(
				JSON.stringify({
					type: 'audio.session.update',
					model,
					include,
				}),
			);
			return;
		}

		this.socket.send(
			JSON.stringify({
				type: 'audio.session.update',
				model,
				include,
				vad_type,
				eagerness,
				instructions,
				noise_detection,
			}),
		);
	}

	sendAudio(audio: string): void {
		this.socket.send(
			JSON.stringify({ audio, type: ConvoAIWebSocketClientMessageType.AudioBufferAppend }),
		);
	}

	commitAudio(): void {
		this.socket.send(JSON.stringify({ type: ConvoAIWebSocketClientMessageType.AudioBufferCommit }));
	}

	connect(): Promise<ConvoAIWebSocketAPI> {
		return new Promise((resolve, reject) => {
			this.socket.onopen = () => resolve(this);
			this.socket.onerror = (event) => reject(event);
		});
	}

	onServerMessage = (event: MessageEvent): void => {
		const data = JSON.parse(event.data) as ConvoAIWebSocketServerMessage;
		this.onMessage(data);
	};

	disconnect(): void {
		this.socket?.close();
	}

	static create(
		cloudID: string,
		product: string,
		experience: string,
		onMessage: (event: ConvoAIWebSocketServerMessage) => void,
	): Promise<ConvoAIWebSocketAPI> {
		const api = new ConvoAIWebSocketAPI(cloudID, product, experience, onMessage);
		return api.connect();
	}
}
